import React, { useRef, useEffect, useCallback, memo, forwardRef, useImperativeHandle } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ServerURL } from '../ServerURL';
import { formatTimepoint } from './utils';
import { AUDIO_CONFIG } from './audioPlayer/constants';

// Import refactored modules
import { useAudioState, useTimelineDrawing, useAudioStreaming } from './audioPlayer/hooks';
import { getSimpleLayout, useSimpleContainerWidth } from './audioPlayer/simpleLayout';
import { getCachedAudio, setCachedAudio } from './audioPlayer/audioCache';
import { createControlHandlers } from './audioPlayer/controlHandlers';
import { createAudioEventHandlers } from './audioPlayer/audioEventHandlers';
import PlaybackControls from './audioPlayer/PlaybackControls';
import TimeDisplay from './audioPlayer/TimeDisplay';
import AudioControls from './audioPlayer/AudioControls';
import Timeline from './audioPlayer/Timeline';

const StreamingAudioPlayer = memo(forwardRef(({ 
    meetingId,
  meetingDuration = null, 
  segments = [], 
  speakers = [], 
  onTimeUpdate,
  onPreviousCard,
  onNextCard,
  onCardFocusChange
}, ref) => {
  // Refs
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const streamControllerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const playerRef = useRef(null);

  // Custom hooks
  const audioState = useAudioState(meetingDuration);
  const { drawTimeline, forceRedraw } = useTimelineDrawing(
    canvasRef, 
    segments, 
    audioState.currentTime, 
    audioState.duration, 
    audioState.streamedDuration
  );
  const { loadStreamingAudio } = useAudioStreaming(
    meetingId, 
    audioRef, 
    streamControllerRef, 
    audioState,
    ServerURL,
    formatTimepoint,
    getCachedAudio,
    setCachedAudio
  );

  // Simple responsive layout system
  const containerWidth = useSimpleContainerWidth(playerRef);
  const layout = getSimpleLayout(containerWidth);

  // Expose seek functionality and current time via ref
  useImperativeHandle(ref, () => ({
    seekToTime: (timeInSeconds, shouldAutoPlay = true) => {
      console.log('🎯 Seeking to:', formatTimepoint(timeInSeconds));
      audioState.setCurrentTime(timeInSeconds);
      drawTimeline();
      audioState.setHasStreamInitiated(true);
      loadStreamingAudio(Math.floor(timeInSeconds), shouldAutoPlay);
    },
    getCurrentTime: () => audioState.currentTime,
    isPlaying: () => audioState.isPlaying
  }), [loadStreamingAudio, audioState, drawTimeline]);

  // Animation frame update - throttled for better performance
  const updateFrame = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      const absoluteTime = audioRef.current.currentTime + audioState.streamStartOffset;
      const previousTime = audioState.currentTime;
      
      // Only update if time has meaningfully changed (reduces unnecessary redraws)
      if (Math.abs(absoluteTime - previousTime) > AUDIO_CONFIG.ANIMATION_FRAME_THROTTLE) {
        audioState.setCurrentTime(absoluteTime);
        drawTimeline();
      }
      
      animationFrameRef.current = requestAnimationFrame(updateFrame);
    }
  }, [drawTimeline, audioState.streamStartOffset, audioState.setCurrentTime, audioState.currentTime]);

  // Create event handlers
  const audioEventHandlers = createAudioEventHandlers(
    audioState,
    audioRef,
    animationFrameRef,
    updateFrame,
    loadStreamingAudio,
    onTimeUpdate,
    meetingDuration
  );

  // Create control handlers
  const controlHandlers = createControlHandlers(
    audioState,
    audioRef,
    canvasRef,
    loadStreamingAudio,
    drawTimeline,
    formatTimepoint,
    onCardFocusChange
  );



  // Initialize audio element with event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Add event listeners
    const eventMap = {
      'loadedmetadata': audioEventHandlers.handleLoadedMetadata,
      'timeupdate': audioEventHandlers.handleTimeUpdate,
      'play': audioEventHandlers.handlePlay,
      'pause': audioEventHandlers.handlePause,
      'error': audioEventHandlers.handleError,
      'progress': audioEventHandlers.handleProgress,
      'ended': audioEventHandlers.handleEnded
    };

    Object.entries(eventMap).forEach(([event, handler]) => {
      audio.addEventListener(event, handler);
    });

    return () => {
      // Remove event listeners
      Object.entries(eventMap).forEach(([event, handler]) => {
        audio.removeEventListener(event, handler);
      });

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [audioEventHandlers]);

  // Reset state when meetingId changes
  useEffect(() => {
    if (meetingId) {
      console.log('🎵 Meeting audio ready for streaming:', meetingId);
      audioState.resetState();

      // Abort any ongoing streams (clearing src stops network)
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      }
    }
  }, [meetingId, audioState.resetState]);

  // Redraw timeline when data changes
  useEffect(() => {
    drawTimeline();
  }, [drawTimeline]);

  // Force redraw when canvas size changes (e.g., during hover transitions)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeObserver = new ResizeObserver(() => {
      // Use forceRedraw to ensure background is redrawn after resize
      forceRedraw();
    });

    resizeObserver.observe(canvas);

    return () => {
      resizeObserver.disconnect();
    };
  }, [forceRedraw]);

  // Status display logic - only show error messages
  const getStatusText = () => {
    if (audioState.error) return audioState.error;
    return '';
  };

  // Render component
  return (
    <Box
      ref={playerRef}
      sx={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        py: layout.padding.py,
        px: layout.padding.px,
        minHeight: layout.minHeight
      }}
    >
      {/* Hidden audio element */}
      <audio ref={audioRef} preload="none" style={{ display: 'none' }} />

                        {/* Responsive Layout - Single or Two Line */}
      {layout.isTwoLine ? (
        // Two-line layout for very narrow containers
        <Stack spacing={0.5} sx={{ width: '100%' }}>
          {/* First Line: Playback + Time */}
          <Stack 
            direction="row" 
            alignItems="center" 
            spacing={layout.spacing}
            sx={{
              width: '100%',
              justifyContent: 'space-between'
            }}
          >
            <PlaybackControls 
              audioState={audioState}
              controlHandlers={controlHandlers}
              layout={layout}
              segments={segments}
              onPreviousCard={onPreviousCard}
              onNextCard={onNextCard}
              onCardFocusChange={onCardFocusChange}
            />
            <TimeDisplay 
              currentTime={audioState.currentTime}
              duration={audioState.duration}
              formatTimepoint={formatTimepoint}
              layout={layout}
            />
          </Stack>

          {/* Second Line: Controls (centered) */}
          <Stack 
            direction="row" 
            alignItems="center" 
            spacing={layout.spacing}
            sx={{
              width: '100%',
              justifyContent: 'center'
            }}
          >
            <AudioControls 
              audioState={audioState}
              controlHandlers={controlHandlers}
              layout={layout}
              meetingId={meetingId}
              ServerURL={ServerURL}
            />
          </Stack>
        </Stack>
      ) : (
        // Single-line layout
        <Stack 
          direction="row" 
          alignItems="center" 
          spacing={layout.spacing}
          sx={{
            width: '100%',
            overflow: 'visible' // Allow volume slider knob to be visible
          }}
        >
          {/* Playback Controls - Always visible */}
          <PlaybackControls 
            audioState={audioState}
            controlHandlers={controlHandlers}
            layout={layout}
            segments={segments}
            onPreviousCard={onPreviousCard}
            onNextCard={onNextCard}
            onCardFocusChange={onCardFocusChange}
          />

          {/* Time Display - Always visible but compressed on small sizes */}
          <TimeDisplay 
            currentTime={audioState.currentTime}
            duration={audioState.duration}
            formatTimepoint={formatTimepoint}
            layout={layout}
          />

          {/* Flexible spacer - grows/shrinks to maintain right alignment */}
          <Box sx={{ 
            flex: 1, 
            minWidth: layout.useSpacer ? 20 : 5
          }} />

          {/* Audio Controls - Always visible, right-aligned */}
          <AudioControls 
            audioState={audioState}
            controlHandlers={controlHandlers}
            layout={layout}
            meetingId={meetingId}
            ServerURL={ServerURL}
          />
        </Stack>
      )}

      {/* Status - only show errors */}
      {audioState.error && (
        <Typography variant="body2" color="error" sx={{ 
          fontSize: '0.75rem', 
          width: '100%',
          mt: 0.5
        }}>
          {getStatusText()}
        </Typography>
      )}

      {/* Timeline */}
      <Timeline 
        canvasRef={canvasRef}
        segments={segments}
        currentTime={audioState.currentTime}
        duration={audioState.duration}
        onTimelineClick={controlHandlers.handleTimelineClick}
        formatTimepoint={formatTimepoint}
      />

      {/* Error Display */}
      {audioState.error && (
        <Typography color="error" variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
          {audioState.error}
        </Typography>
      )}

      {/* Development Indicators */}
      {process.env.NODE_ENV === 'development' && (
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          {/* Layout Debug Info */}
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.65rem', 
              color: 'info.main',
              opacity: 0.7
            }}
          >
            📱 Layout: {layout.name} ({layout.containerWidth}px) {layout.isTwoLine ? '📱2L' : '📱1L'}
          </Typography>
          

        </Stack>
      )}
    </Box>
  );
}));

StreamingAudioPlayer.displayName = 'StreamingAudioPlayer';

export default StreamingAudioPlayer;